import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { localToday } from '../utils/dateUtils';

const colorTipo = (tipo) => {
  const map = { 'Audiencia': '#0d9488', 'Vencimiento procesal': '#ea580c', 'Consulta / Turno': '#0891b2', 'Reunión': '#4f46e5', 'Vencimiento de deuda': '#ea580c' };
  return map[tipo] || '#9ca3af';
};

function clientesDeExpediente(exp) {
  if (!exp?.expedientes_clientes?.length) return '';
  return exp.expedientes_clientes.map(ec => `${ec.clientes?.apellido || ''} ${ec.clientes?.nombre || ''}`.trim()).filter(Boolean).join(', ');
}
function clientesDeTramite(tr) {
  if (!tr?.tramites_clientes?.length) return '';
  return tr.tramites_clientes.map(tc => `${tc.clientes?.apellido || ''} ${tc.clientes?.nombre || ''}`.trim()).filter(Boolean).join(', ');
}

const META = {
  completada:   { color: '#10b981', icon: '✓', badge: null,        strike: true  },
  realizada:    { color: '#3b82f6', icon: '✓', badge: 'realizada', strike: false },
  descartada:   { color: '#94a3b8', icon: '✕', badge: 'descartada',strike: true  },
  cancelada:    { color: '#6b7280', icon: '✕', badge: 'cancelada', strike: false },
  'por-resolver': { color: '#f59e0b', icon: '!', badge: 'sin resolver', strike: false },
};

// Props:
//   expedienteId, clienteId  — filtros opcionales
//   mode: 'page' | 'widget'
//   onVerTarea(tarea), onOpenEdit(evento), onNavigateExpediente(id), onNavigateTramite(id)
export default function HistorialPanel({
  expedienteId = null,
  clienteId = null,
  mode = 'page',
  onVerTarea,
  onOpenEdit,
  onNavigateExpediente,
  onNavigateTramite,
}) {
  const [tareasCompletadas, setTareasCompletadas] = useState([]);
  const [tareasDescartadas, setTareasDescartadas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [historialTipo, setHistorialTipo] = useState('todo');
  const [historialMes, setHistorialMes] = useState('');
  const [historialBitacora, setHistorialBitacora] = useState(false);
  const [gruposCerrados, setGruposCerrados] = useState(new Set());
  const [marcandoId, setMarcandoId] = useState(null);

  const today = localToday();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const sel = '*, expedientes(caratula, numero, expedientes_clientes(clientes(nombre, apellido))), tramites(nombre, tramites_clientes(clientes(nombre, apellido)))';

    let qC = supabase.from('tareas').select(sel).eq('completada', true).eq('eliminado', false).order('completada_en', { ascending: false });
    let qD = supabase.from('tareas').select(sel).eq('descartada', true).eq('eliminado', false).order('descartada_en', { ascending: false });
    if (expedienteId) { qC = qC.eq('expediente_id', expedienteId); qD = qD.eq('expediente_id', expedienteId); }

    let qEv = supabase.from('agenda')
      .select('*, clientes(nombre, apellido), expedientes(numero, caratula)')
      .eq('eliminado', false)
      .order('fecha', { ascending: false });
    if (expedienteId) qEv = qEv.eq('expediente_id', expedienteId);
    if (clienteId)    qEv = qEv.eq('cliente_id', clienteId);

    const [{ data: tC }, { data: tD }, { data: ev }] = await Promise.all([qC, qD, qEv]);
    setTareasCompletadas(tC || []);
    setTareasDescartadas(tD || []);
    setEventos(ev || []);
    setLoading(false);
  }, [expedienteId, clienteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function setEstadoEvento(id, estado) {
    const prev = eventos;
    setMarcandoId(id);
    setEventos(cur => cur.map(e => e.id === id ? { ...e, estado } : e));
    const { error } = await supabase.from('agenda').update({ estado }).eq('id', id);
    if (error) setEventos(prev);
    setMarcandoId(null);
  }

  // Construir entradas unificadas
  const entries = [];
  tareasCompletadas.forEach(t => entries.push({ key: 'tc' + t.id, kind: 'tarea', estado: 'completada', ts: t.completada_en, dateKey: t.completada_en ? t.completada_en.split('T')[0] : 'sin-fecha', raw: t }));
  eventos.filter(e => e.estado === 'realizado').forEach(e => entries.push({ key: 'ar' + e.id, kind: 'audiencia', estado: 'realizada', ts: e.fecha ? `${e.fecha}T${e.hora || '00:00'}` : null, dateKey: e.fecha || 'sin-fecha', raw: e }));
  eventos.filter(e => e.estado === 'pendiente' && e.fecha && e.fecha < today).forEach(e => entries.push({ key: 'ap' + e.id, kind: 'audiencia', estado: 'por-resolver', ts: `${e.fecha}T${e.hora || '00:00'}`, dateKey: e.fecha, raw: e }));
  if (historialBitacora) {
    tareasDescartadas.forEach(t => entries.push({ key: 'td' + t.id, kind: 'tarea', estado: 'descartada', ts: t.descartada_en, dateKey: t.descartada_en ? t.descartada_en.split('T')[0] : 'sin-fecha', raw: t }));
    eventos.filter(e => e.estado === 'cancelado').forEach(e => entries.push({ key: 'ac' + e.id, kind: 'audiencia', estado: 'cancelada', ts: e.fecha ? `${e.fecha}T${e.hora || '00:00'}` : null, dateKey: e.fecha || 'sin-fecha', raw: e }));
  }

  const q = busqueda.toLowerCase();
  const filtrados = entries.filter(en => {
    if (historialTipo === 'tareas'    && en.kind !== 'tarea')    return false;
    if (historialTipo === 'audiencias'&& en.kind !== 'audiencia') return false;
    if (historialMes && (en.dateKey === 'sin-fecha' || !en.dateKey.startsWith(historialMes))) return false;
    if (q) {
      const tt = en.raw.titulo?.toLowerCase() || '';
      const ex = en.raw.expedientes?.caratula?.toLowerCase() || '';
      const tr = en.raw.tramites?.nombre?.toLowerCase() || '';
      if (!tt.includes(q) && !ex.includes(q) && !tr.includes(q)) return false;
    }
    return true;
  });

  const grupos = {};
  filtrados.forEach(en => { (grupos[en.dateKey] = grupos[en.dateKey] || []).push(en); });
  const fechasOrdenadas = Object.keys(grupos).sort((a, b) => {
    if (a === 'sin-fecha') return 1;
    if (b === 'sin-fecha') return -1;
    return b.localeCompare(a);
  });
  fechasOrdenadas.forEach(k => grupos[k].sort((a, b) => (b.ts || '').localeCompare(a.ts || '')));

  const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
  const ayerStr = ayer.toISOString().split('T')[0];
  const formatFechaGrupo = (key) => {
    if (key === 'sin-fecha') return 'Sin fecha registrada';
    if (key === today) return 'Hoy';
    if (key === ayerStr) return 'Ayer';
    return new Date(key + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Meses disponibles para filtro
  const mesesDisponibles = [...new Set(
    [...tareasCompletadas.map(t => t.completada_en), ...eventos.filter(e => e.estado === 'realizado' || (e.estado === 'pendiente' && e.fecha < today)).map(e => e.fecha)]
      .filter(Boolean).map(d => d.substring(0, 7))
  )].sort().reverse();

  if (mode === 'widget') {
    if (loading) return <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Cargando…</div>;
    const recientes = filtrados.slice(0, 5);
    if (recientes.length === 0) return <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Sin actividad reciente</div>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {recientes.map(en => {
          const t    = en.raw;
          const m    = META[en.estado];
          const isTarea = en.kind === 'tarea';
          return (
            <div key={en.key} onClick={() => isTarea ? onVerTarea?.(t) : onOpenEdit?.(t)}
              style={{ padding: '7px 10px', display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', borderRadius: 7, background: 'var(--surface)', border: '0.5px solid var(--border)' }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ color: '#fff', fontSize: 9, fontWeight: 700 }}>{m.icon}</span>
              </div>
              <span style={{ fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: m.strike ? 'line-through' : 'none' }}>{t.titulo}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {/* Controles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <input className="form-input" placeholder="Buscar en el historial…" value={busqueda} onChange={e => setBusqueda(e.target.value)} style={{ maxWidth: 260 }} />
        <select className="filter-select" value={historialTipo} onChange={e => setHistorialTipo(e.target.value)}>
          <option value="todo">Todo</option>
          <option value="tareas">Tareas</option>
          <option value="audiencias">Eventos</option>
        </select>
        <select className="filter-select" value={historialMes} onChange={e => setHistorialMes(e.target.value)}>
          <option value="">Todos los meses</option>
          {mesesDisponibles.map(m => {
            const [y, mm] = m.split('-');
            const lbl = new Date(+y, +mm - 1, 1).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
            return <option key={m} value={m}>{lbl.charAt(0).toUpperCase() + lbl.slice(1)}</option>;
          })}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)', cursor: 'pointer', userSelect: 'none', marginLeft: 'auto' }}>
          <input type="checkbox" checked={historialBitacora} onChange={e => setHistorialBitacora(e.target.checked)} />
          Bitácora completa
        </label>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)' }}>Cargando…</div>
      ) : filtrados.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✓</div>
          <div className="empty-state-title">{busqueda || historialMes || historialTipo !== 'todo' ? 'Sin resultados' : 'Todavía no hay actividad registrada'}</div>
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {fechasOrdenadas.map(fechaKey => {
            const cerrado = gruposCerrados.has(fechaKey);
            const items   = grupos[fechaKey];
            return (
              <div key={fechaKey} style={{ border: '1px solid var(--border2)', borderRadius: 10, overflow: 'hidden' }}>
                <div
                  onClick={() => setGruposCerrados(prev => { const next = new Set(prev); next.has(fechaKey) ? next.delete(fechaKey) : next.add(fechaKey); return next; })}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface2)', cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', textTransform: 'capitalize' }}>{formatFechaGrupo(fechaKey)}</span>
                    <span style={{ fontSize: 11, color: 'var(--text4)', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, padding: '1px 8px' }}>{items.length}</span>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text4)' }}>{cerrado ? '▶' : '▼'}</span>
                </div>
                {!cerrado && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {items.map(en => {
                      const t = en.raw;
                      const isTarea = en.kind === 'tarea';
                      const m = META[en.estado];
                      const clientesTxt = isTarea
                        ? (t.expedientes ? clientesDeExpediente(t.expedientes) : clientesDeTramite(t.tramites))
                        : (t.clientes ? `${t.clientes.apellido || ''} ${t.clientes.nombre || ''}`.trim() : (t.cliente_manual || null));
                      const hora = isTarea
                        ? (en.ts ? new Date(en.ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : null)
                        : (t.hora ? t.hora.substring(0, 5) : null);
                      const sustantivo = { 'Audiencia': 'la audiencia', 'Consulta / Turno': 'el turno', 'Reunión': 'la reunión', 'Vencimiento procesal': 'el vencimiento', 'Vencimiento de deuda': 'el vencimiento' }[t.tipo] || 'el evento';
                      const expDisplay = t.expedientes;
                      const marcando  = marcandoId === t.id;
                      return (
                        <div key={en.key} onClick={() => isTarea ? onVerTarea?.(t) : onOpenEdit?.(t)}
                          style={{ padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer', borderBottom: '1px solid var(--border2)', background: 'var(--surface)' }}>
                          <div style={{ marginTop: 2, width: 16, height: 16, borderRadius: 3, background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>{m.icon}</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', textDecoration: m.strike ? 'line-through' : 'none' }}>{t.titulo}</div>
                              {hora && <span style={{ fontSize: 11, color: 'var(--text4)', whiteSpace: 'nowrap', flexShrink: 0 }}>{hora}</span>}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: (isTarea ? '#64748b' : colorTipo(t.tipo)) + '22', color: isTarea ? '#64748b' : colorTipo(t.tipo) }}>
                                {isTarea ? 'Tarea' : t.tipo}
                              </span>
                              {m.badge && <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: m.color + '18', color: m.color }}>{m.badge}</span>}
                              {expDisplay && (
                                <span onClick={e => { e.stopPropagation(); onNavigateExpediente?.(t.expediente_id); }} style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }}>
                                  {expDisplay.caratula?.substring(0, 35)}
                                </span>
                              )}
                              {isTarea && t.tramites && (
                                <span onClick={e => { e.stopPropagation(); onNavigateTramite?.(t.tramite_id); }} style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }}>
                                  {t.tramites.nombre?.substring(0, 35)}
                                </span>
                              )}
                              {clientesTxt && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{clientesTxt.substring(0, 30)}</span>}
                            </div>
                            {en.estado === 'por-resolver' && (
                              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <button className="btn btn-ghost btn-xs" disabled={marcando}
                                  onClick={ev => { ev.stopPropagation(); setEstadoEvento(t.id, 'realizado'); }}
                                  style={{ color: 'var(--green)', border: '1px solid var(--green)', opacity: marcando ? 0.6 : 1 }}>
                                  {marcando ? 'Guardando…' : `Se realizó ${sustantivo}`}
                                </button>
                                <button className="btn btn-ghost btn-xs" disabled={marcando}
                                  onClick={ev => { ev.stopPropagation(); setEstadoEvento(t.id, 'cancelado'); }}
                                  style={{ color: 'var(--red)', border: '1px solid var(--red)', opacity: marcando ? 0.6 : 1 }}>
                                  {marcando ? '…' : `Se canceló ${sustantivo}`}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
